/**
 * Authentication Service
 *
 * Type-safe auth service using @monorepo/shared schemas
 */

import {
  type ChangePassword,
  type ChangePasswordResponse,
  ChangePasswordResponseSchema,
  ChangePasswordSchema,
  type CurrentUserResponse,
  CurrentUserResponseSchema,
  type ForgotPassword,
  type ForgotPasswordResponse,
  ForgotPasswordResponseSchema,
  ForgotPasswordSchema,
  type Login,
  type LoginResponse,
  LoginResponseSchema,
  LoginSchema,
  type OAuthSync,
  OAuthSyncSchema,
  type ResetPassword,
  type ResetPasswordResponse,
  ResetPasswordResponseSchema,
  ResetPasswordSchema,
  type Signup,
  type SignupResponse,
  SignupResponseSchema,
  SignupSchema,
  type VerifyEmail,
  type VerifyEmailResponse,
  VerifyEmailResponseSchema,
  VerifyEmailSchema,
} from '@monorepo/shared/schemas/auth'
import { get, post } from '@/lib/api-client'
import { supabase } from '@/shared/lib/supabase/client'

export const authService = {
  /**
   * Login user with email and password
   */
  async login(data: Login): Promise<LoginResponse> {
    // Validate input before sending
    const validated = LoginSchema.parse(data)

    const response = await post('auth/signin', validated, LoginResponseSchema)

    // Store tenant and environment context if available
    if (response.tokens) {
      if (response.tenant?.id) {
        localStorage.setItem('current_tenant_id', response.tenant.id)
      }
      if (response.environment?.id) {
        localStorage.setItem('current_environment_id', response.environment.id)
      }
    }

    return response
  },

  /**
   * Register new user
   */
  async signup(data: Signup): Promise<SignupResponse> {
    const validated = SignupSchema.parse(data)

    const response = await post('auth/signup', validated, SignupResponseSchema)

    // Store tenant context if available (personal tenant auto-created)
    if (response.tokens) {
      if (response.tenant?.id) {
        localStorage.setItem('current_tenant_id', response.tenant.id)
      }
    }

    return response
  },

  /**
   * Request password reset email
   */
  async forgotPassword(data: ForgotPassword): Promise<ForgotPasswordResponse> {
    const validated = ForgotPasswordSchema.parse(data)

    return post('auth/forgot-password', validated, ForgotPasswordResponseSchema)
  },

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPassword): Promise<ResetPasswordResponse> {
    const validated = ResetPasswordSchema.parse(data)

    return post('auth/reset-password', validated, ResetPasswordResponseSchema)
  },

  /**
   * Verify email with token
   */
  async verifyEmail(data: VerifyEmail): Promise<VerifyEmailResponse> {
    const validated = VerifyEmailSchema.parse(data)

    return post('auth/verify-email', validated, VerifyEmailResponseSchema)
  },

  /**
   * Change password (for authenticated users)
   */
  async changePassword(data: ChangePassword): Promise<ChangePasswordResponse> {
    const validated = ChangePasswordSchema.parse(data)

    return post('auth/change-password', validated, ChangePasswordResponseSchema)
  },

  /**
   * Logout user and clear all tokens
   */
  async logout(): Promise<void> {
    try {
      await post('auth/signout')
    } catch {
      // Logout API failure is non-critical
    } finally {
      // Sign out from Supabase
      await supabase.auth.signOut().catch(() => {})

      // Clear tenant/environment context
      localStorage.removeItem('current_tenant_id')
      localStorage.removeItem('current_environment_id')
      localStorage.removeItem('currentTenant')
    }
  },

  /**
   * Get current authenticated user with tenant and environment context
   */
  async getCurrentUser(): Promise<CurrentUserResponse> {
    // Using the new auth/me endpoint
    const response = await get('auth/me', CurrentUserResponseSchema)
    return response
  },

  /**
   * Sync OAuth user after Supabase OAuth redirect
   * Called from the /auth/callback route with tokens from supabase.getSession()
   */
  async syncOAuthUser(data: OAuthSync): Promise<LoginResponse> {
    const validated = OAuthSyncSchema.parse(data)

    const response = await post(
      'auth/oauth/sync',
      validated,
      LoginResponseSchema
    )

    if (response.tenant?.id) {
      localStorage.setItem('current_tenant_id', response.tenant.id)
    }
    if (response.environment?.id) {
      localStorage.setItem('current_environment_id', response.environment.id)
    }

    return response
  },
}
